import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface HeroSectionProps {
  onSignUpClick?: () => void;
  onLoginClick?: () => void;
  isAuthenticated?: boolean;
}

export function HeroSection({ 
  onSignUpClick, 
  onLoginClick, 
  isAuthenticated = false 
}: HeroSectionProps) {
  const handleSignUpClick = () => {
    if (onSignUpClick) {
      onSignUpClick();
    }
  };

  const handleLoginClick = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    },
    hover: {
      scale: 1.05,
      y: -2,
      transition: {
        duration: 0.2,
        ease: "easeOut" as const
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1
      }
    }
  };

  return (
    <section 
      className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20 lg:py-24 overflow-hidden"
      aria-labelledby="hero-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Headline */}
          <motion.h1 
            id="hero-heading"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            variants={itemVariants}
          >
            Track Your Bowling Performance Like Never Before
          </motion.h1>
          
          {/* Subheading */}
          <motion.p 
            className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
            variants={itemVariants}
            aria-describedby="hero-heading"
          >
            Get detailed analytics, compare with league averages, and discover insights that help improve your game.
          </motion.p>
          
          {/* Call-to-Action Buttons */}
          {!isAuthenticated && (
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto sm:max-w-none"
              variants={itemVariants}
            >
              {/* Primary CTA - Get Started */}
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link
                  to="/login"
                  onClick={handleSignUpClick}
                  className="inline-block w-full sm:w-auto min-w-[200px] min-h-[48px] bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation flex items-center justify-center"
                  aria-label="Get started with a free account - no credit card required"
                  role="button"
                >
                  Get Started Free
                </Link>
              </motion.div>
              
              {/* Secondary CTA - Log In */}
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link
                  to="/login"
                  onClick={handleLoginClick}
                  className="inline-block w-full sm:w-auto min-w-[200px] min-h-[48px] border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 px-8 py-3 rounded-lg text-lg font-medium transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 touch-manipulation flex items-center justify-center"
                  aria-label="Sign in to your existing account"
                  role="button"
                >
                  Sign In
                </Link>
              </motion.div>
            </motion.div>
          )}
          
          {/* Authenticated user message */}
          {isAuthenticated && (
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              variants={itemVariants}
            >
              <motion.div
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link
                  to="/dashboard"
                  className="inline-block w-full sm:w-auto min-w-[200px] min-h-[48px] bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation flex items-center justify-center"
                  aria-label="Go to your bowling statistics dashboard"
                  role="button"
                >
                  Go to Dashboard
                </Link>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;